import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Table, Button, Space, message, Modal, Form, Input, Select, DatePicker, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import demoModulesApi, { DemoModule, DemoField, DemoRecord } from '../../lib/demoModulesApi';
import dayjs from 'dayjs';

const DynamicRecordsPage = () => {
  const { moduleKey } = useParams();
  const [module, setModule] = useState<DemoModule | null>(null);
  const [records, setRecords] = useState<DemoRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DemoRecord | null>(null);
  const [form] = Form.useForm();

  const loadModule = async () => {
    if (!moduleKey) return;
    try {
      const data = await demoModulesApi.getModuleByKey(moduleKey);
      setModule(data);
    } catch (error: any) {
      message.error('Failed to load module configuration');
    }
  };

  const loadRecords = async () => {
    if (!moduleKey) return;
    setLoading(true);
    try {
      const data = await demoModulesApi.getRecords(moduleKey);
      setRecords(data.records);
    } catch (error: any) {
      message.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModule();
    loadRecords();
  }, [moduleKey]);

  const handleCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: DemoRecord) => {
    setEditingRecord(record);
    // Convert dates to dayjs
    const formValues = { ...record.data };
    module?.fields?.forEach(field => {
      if (field.dataType === 'date' && formValues[field.key]) {
        formValues[field.key] = dayjs(formValues[field.key]);
      }
    });
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    if (!moduleKey) return;
    try {
      await demoModulesApi.deleteRecord(moduleKey, id);
      message.success('Record deleted');
      loadRecords();
    } catch (error: any) {
      message.error('Failed to delete record');
    }
  };

  const handleSubmit = async (values: any) => {
    if (!moduleKey) return;

    // Convert dayjs to ISO strings
    const data = { ...values };
    module?.fields?.forEach(field => {
      if (field.dataType === 'date' && data[field.key]) {
        data[field.key] = data[field.key].toISOString();
      }
    });

    try {
      if (editingRecord) {
        await demoModulesApi.updateRecord(moduleKey, editingRecord.id, data);
        message.success('Record updated');
      } else {
        // Auto-generate unique number for wo_number like fields
        const uniqueField = module?.fields?.find(f => f.isUnique && f.isReadonly);
        if (uniqueField && !data[uniqueField.key]) {
          data[uniqueField.key] = `${moduleKey?.toUpperCase()}-${Date.now()}`;
        }
        
        await demoModulesApi.createRecord(moduleKey, data);
        message.success('Record created');
      }
      setModalVisible(false);
      loadRecords();
    } catch (error: any) {
      const errorMsg = error.response?.data?.errors?.join(', ') || error.response?.data?.message || 'Operation failed';
      message.error(errorMsg);
    }
  };

  const renderFormField = (field: DemoField) => {
    const commonProps = {
      label: field.label,
      name: field.key,
      rules: field.isRequired ? [{ required: true, message: `${field.label} is required` }] : [],
    };

    // Skip readonly fields in create form
    if (field.isReadonly && !editingRecord) return null;

    switch (field.fieldType) {
      case 'textarea':
        return (
          <Form.Item key={field.key} {...commonProps}>
            <Input.TextArea rows={3} disabled={field.isReadonly} />
          </Form.Item>
        );
      
      case 'select':
        return (
          <Form.Item key={field.key} {...commonProps}>
            <Select disabled={field.isReadonly}>
              {field.options?.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        );
      
      case 'date':
        return (
          <Form.Item key={field.key} {...commonProps}>
            <DatePicker style={{ width: '100%' }} disabled={field.isReadonly} />
          </Form.Item>
        );
      
      case 'number':
        return (
          <Form.Item key={field.key} {...commonProps}>
            <Input type="number" disabled={field.isReadonly} />
          </Form.Item>
        );
      
      default:
        return (
          <Form.Item key={field.key} {...commonProps}>
            <Input disabled={field.isReadonly} />
          </Form.Item>
        );
    }
  };

  // Generate dynamic table columns from fields
  const columns = [
    ...(module?.fields
      ?.filter(f => f.inListView)
      .map(field => ({
        title: field.label,
        dataIndex: field.key,
        key: field.key,
        render: (value: any) => {
          if (field.dataType === 'date') {
            return value ? dayjs(value).format('YYYY-MM-DD') : '-';
          }
          if (field.dataType === 'choice' && field.options) {
            const option = field.options.find(opt => opt.value === value);
            return option?.label || value;
          }
          return value || '-';
        },
      })) || []),
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: DemoRecord) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete record?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0 }}>{module?.name}</h2>
            <p style={{ margin: '4px 0 0', color: '#666' }}>{module?.description}</p>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Create {module?.name.replace(/s$/, '')}
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={records}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title={editingRecord ? `Edit ${module?.name.replace(/s$/, '')}` : `Create ${module?.name.replace(/s$/, '')}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {module?.fields
            ?.filter(f => editingRecord ? f.inEditForm : f.inCreateForm)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(renderFormField)}
        </Form>
      </Modal>
    </div>
  );
};

export default DynamicRecordsPage;
